import { LocationTraffic } from '@raydiant/api-client-js';
import ChartCard from '../ChartCard/ChartCard';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  CHART_DEFAULT_AXIS_OPTIONS,
  CHART_DEFAULT_OPTIONS,
  CHART_DEFAULT_SERIES,
} from '../utils';
import ChartToolTip, { EChartsToolTipParams } from '../ChartToolTip';
import { renderToStaticMarkup } from 'react-dom/server';
import Hidden from 'raydiant-elements/layout/Hidden';
import { Popover } from '@headlessui/react';
import QuestionMarkIcon from '../../../components/QuestionMarkIcon';
import PopoverButton from '../../../components/Popover/PopoverButton';
import { getDisplayValue } from '../ShopperEngagementInsights/utils';
import config from '../../../config';

const { supportAppUrl } = config;

echarts.use([BarChart, LineChart]);
type TopLocationsChartProps = {
  data: LocationTraffic[];
  showAll?: boolean;
};

const capitalize = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1);
};

const transformToLabelText = (value: string) =>
  value
    .split(' ')
    .map((item) => capitalize(item))
    .join(' ');

const getXAxisData = (data: LocationTraffic[]) =>
  data?.map((value) => transformToLabelText(value.locationName));

const getYAxisData = (data: LocationTraffic[], view: keyof LocationTraffic) =>
  data?.map((value) => value[view]);

const TopLocationsChart = ({
  data,
  showAll = false,
}: TopLocationsChartProps) => {
  const { t } = useTranslation();

  const getSeries = (data: LocationTraffic[]) => {
    let series = [];

    series.push({
      ...CHART_DEFAULT_SERIES.bar,
      data: getYAxisData(data, 'ots'),
      name: t('analytics.trafficAwarenessPage.tableHeaders.ots'),
      stack: 'ots',
    });

    if (showAll) {
      series.push({
        ...CHART_DEFAULT_SERIES.bar,
        data: getYAxisData(data, 'impressions'),
        name: t('analytics.trafficAwarenessPage.tableHeaders.impressions'),
        stack: 'impressions',
      });
    }

    series.push({
      ...CHART_DEFAULT_SERIES.line,
      data: getYAxisData(data, 'avgDwellTime'),
      name: t('analytics.trafficAwarenessPage.tableHeaders.avgDwellTime'),
      lineStyle: {
        color: '#1B4368',
        width: 3,
      },
      showSymbol: false,
      yAxisIndex: 1,
    });

    return series;
  };

  const color = showAll
    ? ['#FDE182', '#009DDB', '#1B4368']
    : ['#FDE182', '#1B4368'];

  const options = {
    ...CHART_DEFAULT_OPTIONS,
    color,
    toolbox: {
      feature: {
        saveAsImage: {},
      },
    },
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      data: [
        {
          name: t('analytics.trafficAwarenessPage.tableHeaders.ots'),
          icon: 'square',
        },
        {
          name: t('analytics.trafficAwarenessPage.tableHeaders.impressions'),
          icon: 'square',
        },
        {
          name: t('analytics.trafficAwarenessPage.tableHeaders.avgDwellTime'),
          icon: 'square',
        },
      ],
    },
    xAxis: [
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.xAxis,
        data: getXAxisData(data),
        name: t('analytics.locations').toUpperCase(),
      },
    ],
    yAxis: [
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.yAxis,
        alignTicks: true,
        name: t(
          'analytics.trafficAwarenessPage.tableHeaders.ots',
        ).toUpperCase(),
        axisLabel: {
          ...CHART_DEFAULT_AXIS_OPTIONS.yAxis.axisLabel,
        },
      },
      {
        ...CHART_DEFAULT_AXIS_OPTIONS.yAxis,
        name: `${t(
          'analytics.trafficAwarenessPage.tableHeaders.avgDwellTime',
        )} (${t(
          'analytics.trafficAwarenessPage.tableHeaders.inSeconds',
        )})`.toUpperCase(),
        axisLabel: {
          margin: 8,
        },
      },
    ],
    series: getSeries(data),
    tooltip: {
      ...CHART_DEFAULT_OPTIONS.tooltip,
      formatter: (params: EChartsToolTipParams[]) => {
        const index = params[0]?.dataIndex;
        const value = data[index];

        const modifiedParams: EChartsToolTipParams[] = params.map((item) => {
          if (
            item.seriesName ===
            t('analytics.trafficAwarenessPage.tableHeaders.avgDwellTime')
          ) {
            return { ...item, value: getDisplayValue(item.value, ' sec') };
          } else {
            return {
              ...item,
              value: getDisplayValue(item.value),
            };
          }
        });

        return renderToStaticMarkup(
          <ChartToolTip
            header={
              <p className="font-bold text-sm px-2 py-1 capitalize text-black">
                {transformToLabelText(value.locationName)}
              </p>
            }
            values={modifiedParams}
          />,
        );
      },
    },
  };

  const mobileOptions = {
    ...options,
    legend: {
      ...CHART_DEFAULT_OPTIONS.legend,
      data: [
        {
          name: t('analytics.trafficAwarenessPage.tableHeaders.ots'),
          icon: 'square',
        },
        {
          name: t('analytics.trafficAwarenessPage.tableHeaders.impressions'),
          icon: 'square',
        },
        {
          name: t('analytics.trafficAwarenessPage.tableHeaders.avgViewingTime'),
          icon: 'square',
        },
      ],
      type: 'scroll',
    },
    series: getSeries(data),
  };

  return (
    <ChartCard
      subTitle={''}
      title={
        <div className="flex gap-3 items-center">
          <h2 className="text-black text-xl font-bold">
            {t('analytics.trafficAwarenessPage.topLocationsChart.title')!}
          </h2>
          <Popover className="relative">
            <PopoverButton icon={<QuestionMarkIcon />} />
            <Popover.Panel className="absolute z-10 card popover drop-shadow-xl">
              <div className="overflow-hidden">
                <p className="mb-3">
                  {
                    t(
                      'analytics.trafficAwarenessPage.topLocationsChart.tooltip',
                    )!
                  }
                </p>
                <a
                  href={`${supportAppUrl}/visitor-insight/5imknKqrccCD9gmD6gKuJ1/how-do-i-use-the-insight-analytics-platform/6RjNYsyCvxraPhuSNUMYQo`}
                  rel="noreferrer"
                  target="_blank"
                >
                  {t('analytics.learnmore')}
                </a>
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      }
    >
      <div className="min-w-full min-h-full">
        <Hidden xsDown>
          <ReactECharts
            style={{ height: '410px' }}
            option={options}
            onChartReady={(chart) => chart.resize()}
          />
        </Hidden>
        <Hidden smUp>
          <ReactECharts
            style={{ height: '410px' }}
            option={mobileOptions}
            onChartReady={(chart) => chart.resize()}
          />
        </Hidden>
      </div>
    </ChartCard>
  );
};

export default TopLocationsChart;
